import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AnimalForm from "@/components/livestock/AnimalForm";
import { BellRing, FileSpreadsheet, HeartPulse, Loader2, PawPrint, Plus, RefreshCw, ShieldCheck, Skull, Sparkles, Syringe, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const REPORT_TYPES = [
    { value: "species_report", label: "Species Report" },
    { value: "health_report", label: "Health Report" },
    { value: "weight_report", label: "Weight Report" },
    { value: "breeding_report", label: "Breeding Report" },
    { value: "mortality_report", label: "Mortality Report" },
    { value: "birth_report", label: "Birth Report" },
    { value: "sales_report", label: "Sales Report" },
    { value: "profit_report", label: "Profit Report" },
];

const FIELD_TYPES = ["text", "number", "date", "boolean", "dropdown"];
const CHART_COLORS = ["#2E7D32", "#0288D1", "#F9A825", "#6A1B9A", "#C62828", "#00897B", "#5D4037"];
const FARM_TIMEZONE = "Asia/Manila";

function farmDateIso(date = new Date()) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: FARM_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
}

const LIVESTOCK_ALERT_GROUPS = [
    { key: "birth", label: "Birth", kinds: ["birth", "farrowing_followup"] },
    { key: "vaccination", label: "Vaccination", kinds: ["vaccination"] },
    { key: "medicine", label: "Medicine", kinds: ["withdrawal", "treatment", "follow_up", "medical_schedule", "medical_schedule_completed"] },
    { key: "pregnancy", label: "Pregnancy", kinds: ["gestation_due_today", "gestation_due_soon", "gestation_overdue"] },
    { key: "low_stock", label: "Low Stock", kinds: ["low_stock"] },
    { key: "feed", label: "Feed", kinds: ["feed"] },
    { key: "due_task", label: "Due Tasks", kinds: ["due_task"] },
];

function exportCsv(rows, filename) {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (!safeRows.length) {
        toast.error("No rows to export");
        return;
    }
    const columns = Array.from(new Set(safeRows.flatMap((row) => Object.keys(row))));
    const escape = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = [columns.join(","), ...safeRows.map((row) => columns.map((column) => escape(row[column])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export default function UniversalLivestockPage() {
    const { user, can, isOwner } = useAuth();
    const canDeleteAnimal = String(user?.role_name || "").trim().toLowerCase() === "owner";
    const [tab, setTab] = useState("dashboard");
    const [summary, setSummary] = useState(null);
    const [dashboardSync, setDashboardSync] = useState(null);
    const [species, setSpecies] = useState([]);
    const [animals, setAnimals] = useState([]);
    const [alertsData, setAlertsData] = useState({ counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] });
    const [fieldDefinitions, setFieldDefinitions] = useState([]);
    const [reportRows, setReportRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [registryError, setRegistryError] = useState("");
    const [deleteBusyId, setDeleteBusyId] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAnimal, setEditingAnimal] = useState(null);
    const [dialogSpeciesId, setDialogSpeciesId] = useState("");
    const [q, setQ] = useState("");
    const [speciesFilter, setSpeciesFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [reportType, setReportType] = useState("species_report");
    const [configSpeciesId, setConfigSpeciesId] = useState("");
    const [configForm, setConfigForm] = useState(null);
    const [fieldDialog, setFieldDialog] = useState(null);
    const [aiStatus, setAiStatus] = useState(null);
    const [livestockInsight, setLivestockInsight] = useState(null);
    const [livestockInsightError, setLivestockInsightError] = useState("");
    const [livestockInsightLoading, setLivestockInsightLoading] = useState(false);
    const [breedingInsight, setBreedingInsight] = useState(null);
    const [breedingInsightError, setBreedingInsightError] = useState("");
    const [breedingInsightLoading, setBreedingInsightLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setRegistryError("");
        try {
            const [summaryData, dashboardData, speciesData, animalData, alertsPayload, fieldData, aiStatusData] = await Promise.all([
                api.get("/livestock/summary").then((response) => response.data),
                api.get("/livestock/dashboard-sync").then((response) => response.data).catch(() => null),
                api.get("/species").then((response) => response.data),
                api.get("/animals").then((response) => response.data),
                api.get("/alerts").then((response) => response.data).catch(() => ({ counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] })),
                api.get("/field-definitions").then((response) => response.data).catch(() => []),
                api.get("/ai/status").then((response) => response.data).catch(() => null),
            ]);
            setSummary(summaryData || null);
            setDashboardSync(dashboardData || null);
            setSpecies(Array.isArray(speciesData) ? speciesData : []);
            setAnimals(Array.isArray(animalData) ? animalData : []);
            setAlertsData(alertsPayload || { counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] });
            setFieldDefinitions(Array.isArray(fieldData) ? fieldData : []);
            setAiStatus(aiStatusData || null);
            setConfigSpeciesId((current) => current || (speciesData?.[0]?.id || ""));
        } catch (error) {
            const detail = formatApiErrorDetail(error.response?.data?.detail) || "Failed to load livestock module";
            setRegistryError(detail);
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    }, []);

    const requestLivestockInsight = async () => {
        setLivestockInsightLoading(true);
        setLivestockInsightError("");
        try {
            const response = await api.post("/ai/insights/livestock", { period: "month" });
            setLivestockInsight(response.data || null);
        } catch (error) {
            const detail = formatApiErrorDetail(error.response?.data?.detail) || "Livestock AI insight is temporarily unavailable";
            setLivestockInsight(null);
            setLivestockInsightError(detail);
        } finally {
            setLivestockInsightLoading(false);
        }
    };

    const requestBreedingInsight = async () => {
        setBreedingInsightLoading(true);
        setBreedingInsightError("");
        try {
            const response = await api.post("/ai/insights/breeding-gestation", { period: "current" });
            setBreedingInsight(response.data || null);
        } catch (error) {
            const detail = formatApiErrorDetail(error.response?.data?.detail) || "Breeding and Gestation AI insight is temporarily unavailable";
            setBreedingInsight(null);
            setBreedingInsightError(detail);
        } finally {
            setBreedingInsightLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        let cancelled = false;
        const loadReport = async () => {
            setReportLoading(true);
            try {
                const todayIso = farmDateIso();
                const currentMonthStart = `${todayIso.slice(0, 7)}-01`;
                const useMonthlyWindow = ["birth_report", "sales_report", "profit_report"].includes(reportType);
                const params = useMonthlyWindow ? { from_date: currentMonthStart, to_date: todayIso } : undefined;
                const response = await api.get(`/reports/${reportType}`, { params });
                if (!cancelled) {
                    setReportRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
                }
            } catch (error) {
                if (!cancelled) {
                    setReportRows([]);
                    toast.error(formatApiErrorDetail(error.response?.data?.detail) || `Failed to load ${reportType}`);
                }
            } finally {
                if (!cancelled) {
                    setReportLoading(false);
                }
            }
        };
        loadReport();
        return () => {
            cancelled = true;
        };
    }, [reportType]);

    const speciesMap = useMemo(() => Object.fromEntries((species || []).map((row) => [row.id, row])), [species]);
    const statusOptions = useMemo(() => {
        const values = new Set();
        animals.forEach((animal) => values.add(String(animal.status || "active")));
        return Array.from(values).sort();
    }, [animals]);

    const filteredAnimals = useMemo(() => {
        return (animals || []).filter((animal) => {
            if (speciesFilter !== "all" && animal.species_id !== speciesFilter) return false;
            if (statusFilter !== "all" && String(animal.status || "") !== statusFilter) return false;
            if (q.trim()) {
                const needle = q.trim().toLowerCase();
                const haystack = [animal.tag_id, animal.name, animal.breed, animal.species_name, animal.assigned_area, animal.pen].join(" ").toLowerCase();
                if (!haystack.includes(needle)) return false;
            }
            return true;
        });
    }, [animals, q, speciesFilter, statusFilter]);

    const selectedConfigSpecies = useMemo(() => species.find((row) => row.id === configSpeciesId) || null, [species, configSpeciesId]);
    useEffect(() => {
        if (!selectedConfigSpecies) return;
        setConfigForm({
            name: selectedConfigSpecies.name || "",
            breed_options: (selectedConfigSpecies.breed_options || []).join(", "),
            status_options: (selectedConfigSpecies.status_options || []).join(", "),
            source_options: (selectedConfigSpecies.source_options || []).join(", "),
            location_labels: (selectedConfigSpecies.location_labels || []).join(", "),
            sex_options: (selectedConfigSpecies.sex_options || []).join(", "),
            gestation_days: selectedConfigSpecies.gestation_days || 0,
            target_weight: selectedConfigSpecies.target_weight || 0,
            price_per_kg: selectedConfigSpecies.price_per_kg || 0,
        });
    }, [selectedConfigSpecies]);

    const dialogSpecies = useMemo(() => species.find((row) => row.id === dialogSpeciesId) || species.find((row) => row.id === editingAnimal?.species_id) || species[0] || null, [species, dialogSpeciesId, editingAnimal]);
    const dialogFields = useMemo(() => fieldDefinitions.filter((field) => String(field.species_id || "") === String((dialogSpecies || {}).id || "")), [fieldDefinitions, dialogSpecies]);
    const speciesChartRows = useMemo(() => Object.entries(summary?.species_breakdown || {}).map(([name, value]) => ({ name, value })), [summary]);
    const statusChartRows = useMemo(() => Object.entries(summary?.status_breakdown || {}).map(([name, value]) => ({ name: name.replace(/_/g, " "), value })), [summary]);
    const notificationGroups = useMemo(() => {
        const relevantAlerts = (alertsData?.alerts || []).filter((alert) => LIVESTOCK_ALERT_GROUPS.some((group) => group.kinds.includes(String(alert.kind || ""))));
        return LIVESTOCK_ALERT_GROUPS.map((group) => ({
            ...group,
            items: relevantAlerts.filter((alert) => group.kinds.includes(String(alert.kind || ""))),
        })).filter((group) => group.items.length > 0);
    }, [alertsData]);
    const dashboardKpis = dashboardSync?.kpis || summary?.kpis || {};

    const exportExcel = async () => {
        if (!reportRows.length) {
            toast.error("No rows to export");
            return;
        }
        const XLSX = await import("xlsx");
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(reportRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Livestock Report");
        const buffer = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `livestock-${reportType}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const exportPdf = () => {
        if (!reportRows.length) {
            toast.error("No rows to export");
            return;
        }
        const columns = Object.keys(reportRows[0]);
        const doc = new jsPDF({ orientation: columns.length > 6 ? "landscape" : "portrait" });
        doc.setFontSize(14);
        doc.text(`Livestock Report - ${REPORT_TYPES.find((item) => item.value === reportType)?.label || reportType}`, 14, 16);
        autoTable(doc, {
            startY: 24,
            head: [columns.map((column) => column.replace(/_/g, " "))],
            body: reportRows.map((row) => columns.map((column) => row[column])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [46, 125, 50] },
        });
        doc.save(`livestock-${reportType}.pdf`);
    };

    const saveSpeciesConfig = async () => {
        if (!selectedConfigSpecies || !configForm) return;
        const parseList = (value) => String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
        try {
            await api.put(`/species/${selectedConfigSpecies.id}`, {
                ...selectedConfigSpecies,
                gestation_days: Number(configForm.gestation_days) || 0,
                target_weight: Number(configForm.target_weight) || 0,
                price_per_kg: Number(configForm.price_per_kg) || 0,
                breed_options: parseList(configForm.breed_options),
                status_options: parseList(configForm.status_options),
                source_options: parseList(configForm.source_options),
                location_labels: parseList(configForm.location_labels),
                sex_options: parseList(configForm.sex_options),
            });
            toast.success("Species configuration saved");
            load();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to save species config");
        }
    };

    const saveFieldDefinition = async () => {
        if (!fieldDialog || !configSpeciesId) return;
        const payload = {
            species_id: configSpeciesId,
            label: fieldDialog.label,
            field_key: fieldDialog.field_key,
            field_type: fieldDialog.field_type,
            section: fieldDialog.section || "Custom",
            required: fieldDialog.required === true,
            options: String(fieldDialog.options || "").split(",").map((item) => item.trim()).filter(Boolean),
        };
        try {
            if (fieldDialog.id) {
                await api.put(`/field-definitions/${fieldDialog.id}`, payload);
            } else {
                await api.post("/field-definitions", payload);
            }
            toast.success("Field definition saved");
            setFieldDialog(null);
            load();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to save field definition");
        }
    };

    const deleteFieldDefinition = async (fieldId) => {
        if (!window.confirm("Delete this custom field?")) return;
        try {
            await api.delete(`/field-definitions/${fieldId}`);
            toast.success("Field deleted");
            load();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete field");
        }
    };

    return (
        <div className="space-y-6" data-testid="universal-livestock-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="tiny-label">{i18n.t("batch2.8b42192e439b")}</div>
                    <h1 className="text-4xl font-bold tracking-tight mt-2">{i18n.t("batch2.d8b795f1caa9")}</h1>
                    <p className="text-muted-foreground mt-2 text-lg">{i18n.t("batch2.be52964f94d6")}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {isOwner() && aiStatus?.enabled && aiStatus?.available && aiStatus?.capabilities?.includes("livestock_summary") ? (
                        <Button variant="outline" onClick={requestLivestockInsight} disabled={livestockInsightLoading} data-testid="livestock-ai-insight-btn">
                            {livestockInsightLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                            {livestockInsightLoading ? "Analyzing…" : "Analyze Livestock"}
                        </Button>
                    ) : null}
                    {isOwner() && aiStatus?.enabled && aiStatus?.available && aiStatus?.capabilities?.includes("breeding_gestation_summary") ? (
                        <Button variant="outline" onClick={requestBreedingInsight} disabled={breedingInsightLoading} data-testid="breeding-gestation-ai-insight-btn">
                            {breedingInsightLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                            {breedingInsightLoading ? "Analyzing…" : "Analyze Breeding & Gestation"}
                        </Button>
                    ) : null}
                    {can("add") ? <Button disabled={species.length === 0} onClick={() => { setEditingAnimal(null); setDialogSpeciesId(species[0]?.id || ""); setDialogOpen(true); }} data-testid="livestock-add-animal-btn"><Plus className="w-4 h-4 mr-1" />{i18n.t("batch2.22d5a3425998")}</Button> : null}
                    <Button variant="outline" onClick={load} disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}{i18n.t("batch2.56e3badc4e6c")}
                    </Button>
                    <Button variant="outline" asChild><Link to="/species">{i18n.t("batch2.49e21486cc6e")}</Link></Button>
                    <Button variant="outline" asChild><Link to="/birth-to-sale">{i18n.t("batch2.5bdd5c241b30")}</Link></Button>
                </div>
            </div>

            {livestockInsight || livestockInsightError ? (
                <Card className="p-4" data-testid="livestock-ai-insight">
                    <div className="flex items-center gap-2 font-semibold"><Sparkles className="w-4 h-4" />{i18n.t("batch2.080a39596b61")}</div>
                    {livestockInsightError ? <p className="mt-3 text-sm text-destructive">{livestockInsightError}</p> : (
                        <>
                            <p className="mt-3 text-sm whitespace-pre-wrap">{livestockInsight?.response}</p>
                            {livestockInsight?.disclaimer && !String(livestockInsight?.response || "").includes(livestockInsight.disclaimer) ? (
                                <p className="mt-3 text-xs text-muted-foreground">{livestockInsight.disclaimer}</p>
                            ) : null}
                        </>
                    )}
                </Card>
            ) : null}

            {breedingInsight || breedingInsightError ? (
                <Card className="p-4" data-testid="breeding-gestation-ai-insight">
                    <div className="flex items-center gap-2 font-semibold"><Sparkles className="w-4 h-4" />{i18n.t("batch2.585c19860651")}</div>
                    {breedingInsightError ? <p className="mt-3 text-sm text-destructive">{breedingInsightError}</p> : (
                        <>
                            <p className="mt-3 text-sm whitespace-pre-wrap">{breedingInsight?.response}</p>
                            {breedingInsight?.disclaimer && !String(breedingInsight?.response || "").includes(breedingInsight.disclaimer) ? (
                                <p className="mt-3 text-xs text-muted-foreground">{breedingInsight.disclaimer}</p>
                            ) : null}
                        </>
                    )}
                </Card>
            ) : null}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap" data-testid="livestock-tabs">
                    <TabsTrigger value="dashboard">{i18n.t("batch2.d87f47b47e4d")}</TabsTrigger>
                    <TabsTrigger value="registry">{i18n.t("batch2.534f84da47a2")}</TabsTrigger>
                    <TabsTrigger value="reports">{i18n.t("batch2.88bc3fe3dadd")}</TabsTrigger>
                    {can("manage_settings") ? <TabsTrigger value="config">{i18n.t("batch2.d8b4d943f628")}</TabsTrigger> : null}
                </TabsList>

                <TabsContent value="dashboard" className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                        <DashboardKpi icon={PawPrint} label={i18n.t("batch2.5dde525d8086")} value={formatNumber(dashboardKpis.total_animals || 0)} />
                        <DashboardKpi icon={ShieldCheck} label={i18n.t("batch2.13b9bfe85a7b")} value={formatNumber(dashboardKpis.active_animals || dashboardKpis.alive_animals || 0)} />
                        <DashboardKpi icon={HeartPulse} label={i18n.t("batch2.ae0433b83254")} value={formatNumber(dashboardKpis.pregnant_animals || dashboardKpis.pregnant || 0)} />
                        <DashboardKpi icon={Syringe} label={i18n.t("batch2.a05f378cba73")} value={formatNumber(dashboardKpis.due_vaccinations || dashboardKpis.vaccinations_this_month || 0)} />
                        <DashboardKpi icon={Skull} label={i18n.t("batch2.fbfb66c84913")} value={formatNumber(dashboardKpis.mortality || dashboardKpis.deaths || dashboardKpis.mortality_this_month || 0)} />
                        <DashboardKpi icon={TrendingUp} label={i18n.t("batch2.c9411ae8d103")} value={formatNumber(dashboardKpis.ready_for_sale || dashboardKpis.market_ready || 0)} />
                        <DashboardKpi icon={Wallet} label={i18n.t("batch2.5d1b80121622")} value={formatCurrency(dashboardKpis.monthly_feed_cost || 0)} />
                        <DashboardKpi icon={PawPrint} label={i18n.t("batch2.6f62975f8e77")} value={`${formatNumber(dashboardKpis.average_weight || 0, { fractionDigits: 2 })} kg`} />
                        <DashboardKpi icon={HeartPulse} label={i18n.t("batch2.037dc426fbbd")} value={formatNumber(dashboardKpis.sick_animals || 0)} />
                        <DashboardKpi icon={Plus} label={i18n.t("batch2.1eb4012ee2c9")} value={formatNumber(dashboardKpis.new_births || dashboardKpis.births || dashboardKpis.births_this_month || 0)} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch2.f588d982d308")}</div>
                            <div className="h-72 min-w-0">
                                <ResponsiveContainer width="100%" height={280} minWidth={280} minHeight={220}>
                                    <PieChart>
                                        <Pie data={speciesChartRows} dataKey="value" nameKey="name" outerRadius={95} label>
                                            {speciesChartRows.map((entry, index) => <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatNumber(value)} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch2.4a26dca78564")}</div>
                            <div className="h-72 min-w-0">
                                <ResponsiveContainer width="100%" height={280} minWidth={280} minHeight={220}>
                                    <BarChart data={statusChartRows} margin={{ top: 8, right: 8, left: -20, bottom: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={50} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip formatter={(value) => formatNumber(value)} />
                                        <Bar dataKey="value" fill="#2E7D32" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </div>

                    <Card className="p-4">
                        <div className="flex items-center gap-2 font-semibold mb-3"><BellRing className="w-4 h-4" />{i18n.t("batch2.77074b7e37f0")}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                            {notificationGroups.map((group) => (
                                <div key={group.key} className="rounded-lg border p-3 bg-muted/20">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <div className="font-medium text-sm">{group.label}</div>
                                        <Badge variant="outline">{group.items.length}</Badge>
                                    </div>
                                    <div className="space-y-2 text-xs">
                                        {group.items.slice(0, 3).map((item) => (
                                            <div key={item.id} className="rounded-md border border-border/60 p-2 bg-background">
                                                <div className="font-medium">{item.title}</div>
                                                <div className="text-muted-foreground">{item.detail}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {notificationGroups.length === 0 ? <div className="text-sm text-muted-foreground">{i18n.t("batch2.509e9a0f3232")}</div> : null}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="registry" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={i18n.t("batch2.ae2279147e20")} className="h-11 md:col-span-2" data-testid="livestock-search-input" />
                            <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.t("batch2.c500690deb24")}</SelectItem>
                                    {species.map((item) => <SelectItem key={item.id} value={item.id}>{item.icon ? `${item.icon} ` : ""}{item.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.t("batch2.6405179d241b")}</SelectItem>
                                    {statusOptions.map((option) => <SelectItem key={option} value={option}>{option.replace(/_/g, " ")}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </Card>

                    {registryError ? (
                        <Card className="p-4 border-destructive/40 bg-destructive/5">
                            <div className="font-medium text-destructive">{i18n.t("batch2.9fdae79fc41c")}</div>
                            <div className="text-sm text-muted-foreground mt-1">{registryError}</div>
                            <Button className="mt-3" variant="outline" onClick={load} disabled={loading}>
                                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                {i18n.t("batch2.9f5cd8a2e880")}
                            </Button>
                        </Card>
                    ) : null}

                    {species.length === 0 ? (
                        <Card className="p-8 text-center">
                            <div className="font-semibold">{i18n.t("batch2.9020d87d6a1d")}</div>
                            <div className="text-sm text-muted-foreground mt-1">{i18n.t("batch2.e1d599cca0a1")}</div>
                            <div className="mt-4">
                                <Button variant="outline" asChild><Link to="/species">{i18n.t("batch2.46f9990edd2b")}</Link></Button>
                            </div>
                        </Card>
                    ) : null}

                    {loading ? (
                        <Card className="p-8 flex items-center justify-center gap-3 text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{i18n.t("batch2.203b14a9d303")}</span>
                        </Card>
                    ) : null}

                    {!loading && species.length > 0 ? (
                    <>
                    <div className="grid grid-cols-1 gap-3 md:hidden">
                        {filteredAnimals.map((animal) => (
                            <Card key={`mobile-${animal.id}`} className="p-4 space-y-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-mono text-sm">{animal.tag_id || animal.id}</div>
                                        <div className="text-sm text-muted-foreground">{animal.species_name || speciesMap[animal.species_id]?.name || "—"}</div>
                                    </div>
                                    <Badge variant="outline">{String(animal.status || "unknown").replace(/_/g, " ")}</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span className="text-muted-foreground">{i18n.t("batch2.346c87feb190")}</span> {animal.breed || "—"}</div>
                                    <div><span className="text-muted-foreground">{i18n.t("batch2.e5af0395ec56")}</span> {animal.sex || "—"}</div>
                                    <div><span className="text-muted-foreground">{i18n.t("batch2.d53879f401fe")}</span> {animal.current_weight != null ? `${formatNumber(animal.current_weight, { fractionDigits: 1 })} kg` : "—"}</div>
                                    <div><span className="text-muted-foreground">{i18n.t("batch2.1bf3994417be")}</span> {animal.assigned_area || animal.pen || "—"}</div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" asChild><Link to={`/animals/${animal.id}`}>{i18n.t("batch2.ff4fc0276e96")}</Link></Button>
                                    {can("edit") ? <Button size="sm" variant="outline" onClick={() => { setEditingAnimal(animal); setDialogSpeciesId(animal.species_id || species[0]?.id || ""); setDialogOpen(true); }}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}
                                    {canDeleteAnimal ? <Button size="sm" variant="destructive" disabled={deleteBusyId === animal.id} onClick={async () => {
                                        if (!window.confirm(`Delete ${animal.tag_id || animal.id}?`)) return;
                                        try {
                                            setDeleteBusyId(animal.id);
                                            await api.delete(`/animals/${animal.id}`);
                                            toast.success("Animal deleted");
                                            load();
                                        } catch (error) {
                                            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete animal");
                                        } finally {
                                            setDeleteBusyId("");
                                        }
                                    }}>{deleteBusyId === animal.id ? "Deleting..." : "Delete"}</Button> : null}
                                </div>
                            </Card>
                        ))}
                        {filteredAnimals.length === 0 ? <Card className="p-8 text-center text-muted-foreground">{i18n.t("batch2.49181eaa9abb")}</Card> : null}
                    </div>

                    <Card className="overflow-hidden hidden md:block">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 text-left">
                                    <tr>
                                        <th className="px-3 py-2">{i18n.t("batch2.71d6ab175c7f")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.83a7238826ab")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.777c078c9b7b")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.e301dd6062f7")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.69c0b81540bd")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.bae7d5be7082")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.d219c68101f5")}</th>
                                        <th className="px-3 py-2">{i18n.t("batch2.6da13addb000")}</th>
                                        <th className="px-3 py-2 text-right">{i18n.t("batch2.c3cd636a585b")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAnimals.map((animal) => (
                                        <tr key={animal.id} className="border-t">
                                            <td className="px-3 py-2 font-mono">{animal.tag_id || animal.id}</td>
                                            <td className="px-3 py-2">{animal.species_name || speciesMap[animal.species_id]?.name || "—"}</td>
                                            <td className="px-3 py-2">{animal.breed || "—"}</td>
                                            <td className="px-3 py-2">{animal.sex || "—"}</td>
                                            <td className="px-3 py-2">{animal.current_weight != null ? `${formatNumber(animal.current_weight, { fractionDigits: 1 })} kg` : "—"}</td>
                                            <td className="px-3 py-2"><Badge variant="outline">{String(animal.status || "unknown").replace(/_/g, " ")}</Badge></td>
                                            <td className="px-3 py-2">{animal.assigned_area || animal.pen || "—"}</td>
                                            <td className="px-3 py-2">{animal.acquisition_type || "—"}</td>
                                            <td className="px-3 py-2 text-right space-x-2">
                                                <Button size="sm" variant="outline" asChild><Link to={`/animals/${animal.id}`}>{i18n.t("batch2.ff4fc0276e96")}</Link></Button>
                                                {can("edit") ? <Button size="sm" variant="outline" onClick={() => { setEditingAnimal(animal); setDialogSpeciesId(animal.species_id || species[0]?.id || ""); setDialogOpen(true); }}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}
                                                {canDeleteAnimal ? <Button size="sm" variant="destructive" disabled={deleteBusyId === animal.id} onClick={async () => {
                                                    if (!window.confirm(`Delete ${animal.tag_id || animal.id}?`)) return;
                                                    try {
                                                        setDeleteBusyId(animal.id);
                                                        await api.delete(`/animals/${animal.id}`);
                                                        toast.success("Animal deleted");
                                                        load();
                                                    } catch (error) {
                                                        toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete animal");
                                                    } finally {
                                                        setDeleteBusyId("");
                                                    }
                                                }}>{deleteBusyId === animal.id ? "Deleting..." : "Delete"}</Button> : null}
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredAnimals.length === 0 ? <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">{i18n.t("batch2.49181eaa9abb")}</td></tr> : null}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                    </>
                    ) : null}
                </TabsContent>

                <TabsContent value="reports" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <Select value={reportType} onValueChange={setReportType}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>{REPORT_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Button className="h-11" variant="outline" onClick={() => exportCsv(reportRows, `livestock-${reportType}.csv`)}>{i18n.t("batch2.3281188351b8")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportExcel}><FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch2.a08e11119aef")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportPdf}>{i18n.t("batch2.d613d88cb2d8")}</Button>
                            <div className="h-11 rounded-md border border-border px-3 grid items-center text-sm text-muted-foreground">{i18n.t("batch2.5d08f9511d95")} {reportLoading ? "Loading..." : reportRows.length}</div>
                        </div>
                    </Card>
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 text-left">
                                    <tr>
                                        {(reportRows[0] ? Object.keys(reportRows[0]) : ["No data"]).map((column) => (
                                            <th key={column} className="px-3 py-2 whitespace-nowrap">{column.replace(/_/g, " ")}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportLoading ? <tr><td className="px-3 py-8 text-center text-muted-foreground">{i18n.t("batch2.d07a4554e6f9")}</td></tr> : null}
                                    {reportRows.map((row, index) => (
                                        <tr key={`${reportType}-${index}`} className="border-t">
                                            {Object.keys(reportRows[0] || {}).map((column) => (
                                                <td key={column} className="px-3 py-2 whitespace-nowrap">{String(row[column] ?? "—")}</td>
                                            ))}
                                        </tr>
                                    ))}
                                    {!reportLoading && reportRows.length === 0 ? <tr><td className="px-3 py-8 text-center text-muted-foreground">{i18n.t("batch2.92928c7b6205")}</td></tr> : null}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </TabsContent>

                {can("manage_settings") ? (
                    <TabsContent value="config" className="space-y-4">
                        <Card className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch2.83a7238826ab")}</Label>
                                    <Select value={configSpeciesId || "__none__"} onValueChange={(value) => setConfigSpeciesId(value === "__none__" ? "" : value)}>
                                        <SelectTrigger className="mt-2 h-11"><SelectValue placeholder={i18n.t("batch2.5c173ef78781")} /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">{i18n.t("batch2.5c173ef78781")}</SelectItem>
                                            {species.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </Card>
                        {configForm && selectedConfigSpecies ? (
                            <>
                                <Card className="p-4 space-y-4">
                                    <div className="font-semibold">{i18n.t("batch2.2f7b242e3a83")}</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <ConfigField label={i18n.t("batch2.6e48b29888d5")} value={configForm.breed_options} onChange={(value) => setConfigForm((current) => ({ ...current, breed_options: value }))} />
                                        <ConfigField label={i18n.t("batch2.c63acbcc4830")} value={configForm.status_options} onChange={(value) => setConfigForm((current) => ({ ...current, status_options: value }))} />
                                        <ConfigField label={i18n.t("batch2.54bce5904e5b")} value={configForm.source_options} onChange={(value) => setConfigForm((current) => ({ ...current, source_options: value }))} />
                                        <ConfigField label={i18n.t("batch2.64257ac78d08")} value={configForm.location_labels} onChange={(value) => setConfigForm((current) => ({ ...current, location_labels: value }))} />
                                        <ConfigField label={i18n.t("batch2.6b7c0218276d")} value={configForm.sex_options} onChange={(value) => setConfigForm((current) => ({ ...current, sex_options: value }))} />
                                        <div className="grid grid-cols-3 gap-3 md:col-span-2">
                                            <div><Label className="tiny-label">{i18n.t("batch2.234d8d89d396")}</Label><Input className="mt-2 h-11" type="number" value={configForm.gestation_days} onChange={(e) => setConfigForm((current) => ({ ...current, gestation_days: e.target.value }))} /></div>
                                            <div><Label className="tiny-label">{i18n.t("batch2.e154cb584579")}</Label><Input className="mt-2 h-11" type="number" value={configForm.target_weight} onChange={(e) => setConfigForm((current) => ({ ...current, target_weight: e.target.value }))} /></div>
                                            <div><Label className="tiny-label">{i18n.t("batch2.73656b6dcc12")}</Label><Input className="mt-2 h-11" type="number" value={configForm.price_per_kg} onChange={(e) => setConfigForm((current) => ({ ...current, price_per_kg: e.target.value }))} /></div>
                                        </div>
                                    </div>
                                    <Button onClick={saveSpeciesConfig}>{i18n.t("batch2.c6cdc5b935cb")}</Button>
                                </Card>

                                <Card className="p-4 space-y-4">
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="font-semibold">{i18n.t("batch2.143b179529ae")}</div>
                                        <Button onClick={() => setFieldDialog({ species_id: configSpeciesId, label: "", field_key: "", field_type: "text", section: "Custom", required: false, options: "" })}><Plus className="w-4 h-4 mr-1" />{i18n.t("batch2.55f9626c6c05")}</Button>
                                    </div>
                                    <div className="space-y-2">
                                        {fieldDefinitions.filter((field) => String(field.species_id || "") === String(configSpeciesId || "")).map((field) => (
                                            <div key={field.id} className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
                                                <div>
                                                    <div className="font-medium">{field.label}</div>
                                                    <div className="text-xs text-muted-foreground">{field.field_key} · {field.field_type} · {field.section || "Custom"}</div>
                                                </div>
                                                <div className="space-x-2">
                                                    <Button size="sm" variant="outline" onClick={() => setFieldDialog({ ...field, options: Array.isArray(field.options) ? field.options.join(", ") : "" })}>{i18n.t("batch2.5301648dcf6b")}</Button>
                                                    <Button size="sm" variant="destructive" onClick={() => deleteFieldDefinition(field.id)}>{i18n.t("batch2.f6fdbe48dc54")}</Button>
                                                </div>
                                            </div>
                                        ))}
                                        {!fieldDefinitions.some((field) => String(field.species_id || "") === String(configSpeciesId || "")) ? <div className="text-sm text-muted-foreground">{i18n.t("batch2.e0a94b30808d")}</div> : null}
                                    </div>
                                </Card>
                            </>
                        ) : null}
                    </TabsContent>
                ) : null}
            </Tabs>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingAnimal ? "Edit Animal" : "Add Animal"}</DialogTitle>
                        <DialogDescription>
                            {editingAnimal ? "Update the selected animal record and save the changes to the livestock registry." : "Create a new livestock record and persist it to the backend registry."}
                        </DialogDescription>
                    </DialogHeader>
                    <AnimalForm
                        key={editingAnimal?.id || (dialogOpen ? "new-animal-open" : "new-animal-closed")}
                        speciesOptions={species}
                        species={dialogSpecies}
                        fields={dialogFields}
                        initial={editingAnimal}
                        onSpeciesChange={setDialogSpeciesId}
                        onSaved={() => { setDialogOpen(false); setEditingAnimal(null); load(); }}
                        onCancel={() => { setDialogOpen(false); setEditingAnimal(null); }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={!!fieldDialog} onOpenChange={(open) => !open && setFieldDialog(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{fieldDialog?.id ? "Edit Custom Field" : "Add Custom Field"}</DialogTitle>
                        <DialogDescription>
                            {i18n.t("batch2.3333d041a6de")}
                        </DialogDescription>
                    </DialogHeader>
                    {fieldDialog ? (
                        <div className="space-y-3">
                            <div><Label className="tiny-label">{i18n.t("batch2.74341e3c271d")}</Label><Input className="mt-2 h-11" value={fieldDialog.label} onChange={(e) => setFieldDialog((current) => ({ ...current, label: e.target.value }))} /></div>
                            <div><Label className="tiny-label">{i18n.t("batch2.7cd5b9ea84fa")}</Label><Input className="mt-2 h-11" value={fieldDialog.field_key} onChange={(e) => setFieldDialog((current) => ({ ...current, field_key: e.target.value }))} /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch2.b6321aaca189")}</Label>
                                    <Select value={fieldDialog.field_type} onValueChange={(value) => setFieldDialog((current) => ({ ...current, field_type: value }))}>
                                        <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                                        <SelectContent>{FIELD_TYPES.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch2.f2c6b564bd81")}</Label><Input className="mt-2 h-11" value={fieldDialog.section} onChange={(e) => setFieldDialog((current) => ({ ...current, section: e.target.value }))} /></div>
                            </div>
                            <div><Label className="tiny-label">{i18n.t("batch2.512dad105fdd")}</Label><Textarea className="mt-2" value={fieldDialog.options} onChange={(e) => setFieldDialog((current) => ({ ...current, options: e.target.value }))} placeholder={i18n.t("batch2.3d9f454fe3cd")} /></div>
                            <div>
                                <Label className="tiny-label">{i18n.t("batch2.eed6bfb41051")}</Label>
                                <Select value={fieldDialog.required ? "yes" : "no"} onValueChange={(value) => setFieldDialog((current) => ({ ...current, required: value === "yes" }))}>
                                    <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="yes">{i18n.t("batch2.5397e0583f14")}</SelectItem>
                                        <SelectItem value="no">{i18n.t("batch2.816c52fd2bdd")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setFieldDialog(null)}>{i18n.t("batch2.77dfd2135f4d")}</Button>
                                <Button onClick={saveFieldDefinition}>{i18n.t("batch2.71ae2e313d19")}</Button>
                            </DialogFooter>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DashboardKpi({ icon: Icon, label, value }) {
    return (
        <Card className="p-4">
            <div className="tiny-label flex items-center gap-2"><Icon className="w-4 h-4" />{label}</div>
            <div className="text-2xl font-bold mt-2">{value}</div>
        </Card>
    );
}

function ConfigField({ label, value, onChange }) {
    return (
        <div>
            <Label className="tiny-label">{label}</Label>
            <Textarea className="mt-2" value={value} onChange={(e) => onChange(e.target.value)} placeholder={i18n.t("batch2.1ee3200b888b")} />
        </div>
    );
}
